/**
 * Link codes and invitation codes, as typed by a human.
 *
 * One implementation, mirroring `normalise()` in services/api/valley/linking.py.
 * The client and the server have to fold a typed code to exactly the same
 * string: the server hashes it to find the row, and the client derives a key
 * from it to open what the row carries. A single character of disagreement
 * shows up as "that did not unlock your family", which points at the wrong
 * thing entirely.
 */

/** Crockford base32: no I, L, O or U. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** Eight for a code that carries nothing, twenty for one that carries a key. */
export const VALID_LENGTHS = [8, 20] as const;

export function normalise(code: string, expected?: number): string {
  const cleaned = code
    .toUpperCase()
    .replace(/[\s-]/g, '')
    // The three glyphs people actually mistype, folded the way the server
    // folds them: I and L read as 1, O reads as 0.
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0');

  const lengths: readonly number[] = expected ? [expected] : VALID_LENGTHS;
  if (!lengths.includes(cleaned.length) || [...cleaned].some((c) => !ALPHABET.includes(c))) {
    throw new Error('That code is not right. Check it and try again.');
  }
  return cleaned;
}

/**
 * Grouped in fours, matching `pretty()` on the server so the same code reads
 * the same wherever it is shown. Presentation only: every hyphen is stripped
 * again on the way back in.
 */
export function format(code: string): string {
  return (code.match(/.{1,4}/g) ?? []).join('-');
}
